import React from 'react';

const Analytics = ({ issues, projects, sprints, filters }) => {
  const { selectedProject, selectedSprint, dateRange } = filters;

  // Filter data based on selected filters
  const filteredIssues = issues.filter(issue => {
    if (selectedProject !== 'all' && issue.projectId !== selectedProject) return false;
    if (selectedSprint !== 'all' && issue.sprintId !== selectedSprint) return false;
    return true;
  });

  // Calculate analytics metrics
  const calculateMetrics = () => {
    const totalIssues = filteredIssues.length;
    const completedIssues = filteredIssues.filter(issue => issue.status === 'done').length;
    const inProgressIssues = filteredIssues.filter(issue => issue.status === 'in-progress').length;
    const todoIssues = filteredIssues.filter(issue => issue.status === 'todo').length;
    
    const avgCompletionTime = calculateAverageCompletionTime(filteredIssues);
    const issueTypeDistribution = calculateIssueTypeDistribution(filteredIssues);
    const priorityDistribution = calculatePriorityDistribution(filteredIssues);
    const teamPerformance = calculateTeamPerformance(filteredIssues);

    return {
      totalIssues,
      completedIssues,
      inProgressIssues,
      todoIssues,
      completionRate: totalIssues > 0 ? (completedIssues / totalIssues) * 100 : 0,
      avgCompletionTime,
      issueTypeDistribution,
      priorityDistribution,
      teamPerformance
    };
  };

  const calculateAverageCompletionTime = (issues) => {
    const completedIssues = issues.filter(issue => 
      issue.status === 'done' && issue.createdAt && issue.updatedAt
    );
    
    if (completedIssues.length === 0) return 0;
    
    const totalTime = completedIssues.reduce((sum, issue) => {
      const created = new Date(issue.createdAt);
      const updated = new Date(issue.updatedAt);
      return sum + (updated - created);
    }, 0);
    
    return Math.round(totalTime / completedIssues.length / (1000 * 60 * 60 * 24)); // Convert to days
  };

  const calculateIssueTypeDistribution = (issues) => {
    const distribution = {
      story: 0,
      task: 0,
      bug: 0,
      epic: 0
    };
    
    issues.forEach(issue => {
      if (distribution.hasOwnProperty(issue.type)) {
        distribution[issue.type]++;
      }
    });
    
    return distribution;
  };

  const calculatePriorityDistribution = (issues) => {
    const distribution = {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    };
    
    issues.forEach(issue => {
      if (distribution.hasOwnProperty(issue.priority)) {
        distribution[issue.priority]++;
      }
    });
    
    return distribution;
  };

  const calculateTeamPerformance = (issues) => {
    const performance = {};
    
    issues.forEach(issue => {
      if (issue.assigneeId) {
        if (!performance[issue.assigneeId]) {
          performance[issue.assigneeId] = {
            name: issue.assigneeName || 'Unknown',
            total: 0,
            completed: 0,
            inProgress: 0
          };
        }
        
        performance[issue.assigneeId].total++;
        if (issue.status === 'done') performance[issue.assigneeId].completed++;
        if (issue.status === 'in-progress') performance[issue.assigneeId].inProgress++;
      }
    });
    
    return performance;
  };

  const metrics = calculateMetrics();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Advanced Analytics
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Issue Type Distribution */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Issue Type Distribution
          </h3>
          <div className="space-y-3">
            {Object.entries(metrics.issueTypeDistribution).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                  {type}
                </span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ 
                        width: `${metrics.totalIssues > 0 ? (count / metrics.totalIssues) * 100 : 0}%` 
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white w-8">
                    {count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority Distribution */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Priority Distribution
          </h3>
          <div className="space-y-3">
            {Object.entries(metrics.priorityDistribution).map(([priority, count]) => (
              <div key={priority} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                  {priority}
                </span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        priority === 'low' ? 'bg-green-500' :
                        priority === 'medium' ? 'bg-yellow-500' :
                        priority === 'high' ? 'bg-orange-500' : 'bg-red-500'
                      }`}
                      style={{ 
                        width: `${metrics.totalIssues > 0 ? (count / metrics.totalIssues) * 100 : 0}%` 
                      }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white w-8">
                    {count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Team Performance */}
        <div className="lg:col-span-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Team Performance
          </h3>
          <div className="space-y-4">
            {Object.entries(metrics.teamPerformance).map(([userId, data]) => (
              <div key={userId} className="flex items-center justify-between p-3 bg-white dark:bg-gray-600 rounded-lg">
                <span className="font-medium text-gray-900 dark:text-white">
                  {data.name}
                </span>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-green-600 dark:text-green-400">
                    {data.completed} completed
                  </span>
                  <span className="text-blue-600 dark:text-blue-400">
                    {data.inProgress} in progress
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {data.total} total
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0}%
                  </span>
                </div>
              </div>
            ))}
            {Object.keys(metrics.teamPerformance).length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No team performance data available
              </p>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {metrics.avgCompletionTime}
            </div>
            <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">
              Avg. Days to Complete
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {metrics.completionRate.toFixed(1)}%
            </div>
            <div className="text-sm text-green-600 dark:text-green-400 mt-1">
              Completion Rate
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {metrics.inProgressIssues}
            </div>
            <div className="text-sm text-purple-600 dark:text-purple-400 mt-1">
              In Progress
            </div>
          </div>
          
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {metrics.todoIssues}
            </div>
            <div className="text-sm text-orange-600 dark:text-orange-400 mt-1">
              To Do
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Summary
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {filteredIssues.length} issues analyzed • 
          {metrics.completedIssues} completed • 
          {metrics.avgCompletionTime} days average completion time
        </p>
      </div>
    </div>
  );
};

export default Analytics;